import moment from 'moment'
import { useCallback, useState } from 'react'

import { useErrorHandler } from '../../hooks/useErrorHandler'
import { Booking } from '../../models/Booking'
import { Dashboard } from '../../models/Dashboard'
import { RecordFactory } from '../../models/RecordFactory'
import { User } from '../../models/User'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import Button from '../app/design/Button'
import Paragraph from '../app/design/Paragraph'
import {
    ClickableTableRow,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../app/design/Table'
import { CancelBookingModal } from '../bookings/CancelBookingModal'
import { MoreMenuButton } from '../common/MoreMenuButton'
import { DashboardSkeleton } from '../common/skeletons/DashboardSkeleton'
import { ClientTableCell } from './ClientTableCell'
import { EmptyDashboardLayout } from './EmptyDashboardLayout'
import { KeyMetric } from './KeyMetric'
import { ProListTableCell } from './ProListTableCell'
import { ServiceListTableCell } from './ServiceListTableCell'

type Props = {
    dashboard?: Dashboard
    bookings: Booking[]
    searchText: string
    onCancelBooking: (booking: Booking) => void
    isLoading: boolean
}

function ConferenceButton({ booking }: { booking: Booking }) {
    if (!booking.allowConference || !booking.conferenceUrlString || !booking.date) {
        return null
    }

    // only show the conference button if the conference starts soon
    const conferenceStartsSoon = booking.date.subtract(15, 'minutes').isBefore(moment())
    const withinHourFromStart = booking.date.add(1, 'hour').isAfter(moment())
    if (!conferenceStartsSoon || !withinHourFromStart) {
        return null
    }

    return (
        <Button
            intent="primary"
            size="small"
            href={booking.conferenceUrlString}
            target="PSVideo"
            className="whitespace-nowrap"
        >
            Join video
        </Button>
    )
}

const viewWillAppear = async (base: PocketComponent<Props>) => {
    return await Promise.allSettled(
        base.props.bookings.map((b) => RecordFactory.getInstance().loadBooking(b.id!))
    )
}

const render = ({
    base: {
        props: { bookings, dashboard, onCancelBooking, searchText, isLoading },
    },
}: PocketComponentProps<ComponentData<typeof viewWillAppear>, Props>) => {
    const { logAndDisplayError } = useErrorHandler()
    const [bookingToDelete, setBookingToDelete] = useState<Booking | null>(null)
    const [isSaving, setIsSaving] = useState(false)

    const handleCancelBooking = useCallback(
        async (notifyClient: boolean) => {
            if (!bookingToDelete) return

            setIsSaving(true)

            const result = await RecordFactory.getInstance().cancelBooking(
                bookingToDelete,
                notifyClient,
                false,
                undefined,
                undefined,
                false
            )
            if (!result.ok) {
                return logAndDisplayError(result.error)
            }

            setBookingToDelete(null)
            setIsSaving(false)

            onCancelBooking(bookingToDelete)
        },
        [bookingToDelete, logAndDisplayError, onCancelBooking]
    )

    if (isLoading) {
        return <DashboardSkeleton />
    }

    return (
        <div className="flex flex-col gap-6">
            <KeyMetric
                metrics={[
                    {
                        title: bookings.length === 1 ? `Booking` : `Bookings`,
                        value: bookings.length.toString(),
                    },
                ]}
            />
            <Table>
                <TableHeader>
                    <TableRow location="head">
                        <TableHead className="font-normal">Client</TableHead>
                        <TableHead className="font-normal">Services</TableHead>
                        <TableHead className="font-normal">Date</TableHead>
                        <TableHead className="font-normal">Time</TableHead>
                        <TableHead className="font-normal">Pro</TableHead>
                        <TableHead></TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {bookings.map((booking) => (
                        <ClickableTableRow
                            key={booking.id}
                            className="cursor-pointer"
                            href={`/bookings/${booking.id}`}
                        >
                            <TableCell>
                                <ClientTableCell
                                    user={booking.client}
                                    subaccount={booking.subaccount}
                                    shouldShowCard={true}
                                />
                            </TableCell>
                            <TableCell>
                                <Paragraph size="sm">
                                    <ServiceListTableCell services={booking.lines} />
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph className="text-sm whitespace-nowrap">
                                    {booking.date?.format('ddd, MMM D, yyyy')}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <Paragraph className="text-sm whitespace-nowrap">
                                    {booking.date?.format('h:mm A')}
                                </Paragraph>
                            </TableCell>
                            <TableCell>
                                <ProListTableCell
                                    pros={
                                        booking.jobs
                                            .map((j) => j.person)
                                            .filter((p) => !!p) as User[]
                                    }
                                    size="xs"
                                />
                            </TableCell>
                            <TableCell>
                                <div className="flex items-center justify-end gap-2">
                                    <ConferenceButton booking={booking} />
                                    <MoreMenuButton
                                        options={[
                                            {
                                                id: 'cancel',
                                                title: 'Cancel',
                                                isDanger: true,
                                            },
                                        ]}
                                        onSelectOption={(option) => {
                                            if (option.id === 'cancel') setBookingToDelete(booking)
                                        }}
                                    />
                                </div>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {bookings.length === 0 && (
                        <TableCell colSpan={10}>
                            <EmptyDashboardLayout dashboard={dashboard} searchtext={searchText} />
                        </TableCell>
                    )}
                </TableBody>
            </Table>

            <CancelBookingModal
                bookings={bookingToDelete ? [bookingToDelete] : []}
                onCancelBooking={handleCancelBooking}
                onClose={() => setBookingToDelete(null)}
                isSaving={isSaving}
            />
        </div>
    )
}

export const BookingsDashboard = usePSComponent(render, viewWillAppear)
